import { useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import type {
  CommunityCanvas,
  CommunityShape,
  CommunityShapeKind,
  CommunityTextBox,
  CommunityTextRole,
  CommunityZonesV2,
  ZoneBox,
} from '../../types/api';
import { BuilderIcon, BuilderMore, Switch, type BuilderIconName } from './BuilderControls';
import { CommunityLayerInspector } from './CommunityLayerInspector';
import { LogoFill, PortraitFill, ShapeFill, TextFill, ValueFill } from './CommunityDesignPreview';
import { backdropLuminance, backdropStyle, boxStyle, textInk } from './communityPreviewStyle';
import {
  findShape,
  findText,
  layerBox,
  layerName,
  movedBox,
  resizedBox,
  shapeKey,
  textKey,
  withLayerBox,
  type LayerKey,
} from './builderLayers';
import {
  DEFAULT_PORTRAIT,
  DEFAULT_VALUE,
  LOGO_STYLE_LABELS,
  SHAPES_MAX,
  SHAPE_KIND_LABELS,
  SHAPE_MINIMUM,
  TEXTS_MAX,
  TEXT_MINIMUM,
  TEXT_ROLES,
  TEXT_ROLE_ORDER,
  clampBox,
  colorName,
  isAiBox,
  newShape,
  newTextBox,
  nextLayerId,
  portraitBox,
  resolveColor,
  type BrandPalette,
  type ResizeCorner,
  type ZoneMinimum,
} from './communityZones';
import { useStageGesture } from './useStageGesture';

/**
 * The design builder: a layers list, a live canvas and an inspector for the
 * one layer in hand.
 *
 * The canvas draws the design with the same pieces `CommunityDesignPreview`
 * uses — so what the author arranges is what the gallery and the brief show —
 * and every layer on it is a real, focusable element that drags, resizes from
 * any corner, and nudges under the arrow keys. Coordinates are fractions of the
 * design, never pixels, and every move goes through the clamps the backend
 * validates with (`communityZones.ts`), so a layer cannot be dragged off the
 * canvas or shrunk under its minimum in the first place.
 *
 * Gestures run on `useStageGesture`: measured once, painted once per frame
 * straight onto the layer and its selection frame, and committed to the map on
 * pointerup. A key press is a decision rather than a gesture, so it commits at
 * once.
 */

/** Arrow-key step, and the Shift-modified step. Mirrors the slide editor. */
const NUDGE_STEP = 0.01;
const NUDGE_STEP_LARGE = 0.05;

function percent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/** One in-flight gesture: which layer, what it is doing, where it started. */
interface Session {
  key: LayerKey;
  mode: 'move' | ResizeCorner;
  start: ZoneBox;
  grab: { dx: number; dy: number };
  next: ZoneBox;
}

const CORNERS: readonly ResizeCorner[] = ['nw', 'ne', 'sw', 'se'];

const CORNER_NAMES: Record<ResizeCorner, string> = {
  nw: 'top-left',
  ne: 'top-right',
  sw: 'bottom-left',
  se: 'bottom-right',
};

export interface CommunityDesignBuilderProps {
  zones: CommunityZonesV2;
  onChange: (zones: CommunityZonesV2) => void;
  palette: BrandPalette;
  /** The signed URL of the design's image, when the backdrop is one. */
  imageUrl: string | null;
  isLoadingImage?: boolean;
  /** The author's own logo, so the logo box previews as theirs. */
  logoUrl: string | null;
  /** The image source controls, shown in the backdrop inspector for an image design. */
  imageSource: ReactNode;
  disabled?: boolean;
}

export function CommunityDesignBuilder({
  zones,
  onChange,
  palette,
  imageUrl,
  isLoadingImage = false,
  logoUrl,
  imageSource,
  disabled = false,
}: CommunityDesignBuilderProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const layerRefs = useRef(new Map<LayerKey, HTMLElement>());
  const sessionRef = useRef<Session | null>(null);
  const gesture = useStageGesture(stageRef);
  const [chosen, setChosen] = useState<LayerKey>(() =>
    zones.texts[0] ? textKey(zones.texts[0].id) : 'backdrop',
  );

  // A deleted or renamed layer drops the selection back to the backdrop,
  // derived here rather than repaired in an effect.
  const selected: LayerKey =
    chosen === 'backdrop' || layerBox(zones, chosen) !== null || chosen === 'logo'
      ? chosen
      : 'backdrop';
  const selectedBox = layerBox(zones, selected);
  const luma = backdropLuminance(zones.canvas, palette);

  function registerLayer(key: LayerKey, element: HTMLElement | null) {
    if (element) layerRefs.current.set(key, element);
    else layerRefs.current.delete(key);
  }

  function select(key: LayerKey) {
    setChosen(key);
  }

  /* ---------------------------- gestures ---------------------------- */

  function paintBox(next: ZoneBox, start: ZoneBox, mode: Session['mode']) {
    gesture.paint((element, stage) => {
      const targets = [element, frameRef.current].filter(
        (target): target is HTMLElement => target !== null,
      );
      for (const target of targets) {
        if (mode === 'move') {
          const dx = (next.x - start.x) * stage.width;
          const dy = (next.y - start.y) * stage.height;
          target.style.transform = `translate(${dx}px, ${dy}px)`;
        } else {
          const style = boxStyle(next);
          target.style.left = String(style.left);
          target.style.top = String(style.top);
          target.style.width = String(style.width);
          target.style.height = String(style.height);
        }
      }
    });
  }

  function finishGesture(element: HTMLElement) {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (!session) return;
    // Back to exactly what React last wrote, so an unchanged box is not left
    // holding an inline value React does not know it has.
    const style = boxStyle(session.start);
    for (const target of [element, frameRef.current]) {
      if (!target) continue;
      target.style.transform = '';
      target.style.left = String(style.left);
      target.style.top = String(style.top);
      target.style.width = String(style.width);
      target.style.height = String(style.height);
    }
    const { next, start } = session;
    if (next.x !== start.x || next.y !== start.y || next.w !== start.w || next.h !== start.h) {
      onChange(withLayerBox(zones, session.key, next));
    }
  }

  function startMove(key: LayerKey, event: PointerEvent<HTMLElement>) {
    // A second finger changes nothing, the selection included.
    if (event.button !== 0 || !gesture.canBegin(event)) return;
    event.stopPropagation();
    select(key);
    if (disabled) return;
    const box = layerBox(zones, key);
    const element = layerRefs.current.get(key);
    if (!box || !element) return;
    const point = gesture.begin(event, element, finishGesture);
    if (!point) return;
    sessionRef.current = {
      key,
      mode: 'move',
      start: box,
      grab: { dx: point.fx - box.x, dy: point.fy - box.y },
      next: box,
    };
    // Suppress the browser's own focus and text selection so focus is moved
    // deliberately — the layer stays keyboard-nudgeable after a drag.
    event.preventDefault();
    element.focus();
  }

  function startResize(corner: ResizeCorner, event: PointerEvent<HTMLElement>) {
    if (disabled || event.button !== 0 || !selectedBox || !gesture.canBegin(event)) return;
    event.stopPropagation();
    const element = layerRefs.current.get(selected);
    if (!element) return;
    if (!gesture.begin(event, element, finishGesture)) return;
    sessionRef.current = {
      key: selected,
      mode: corner,
      start: selectedBox,
      grab: { dx: 0, dy: 0 },
      next: selectedBox,
    };
    event.preventDefault();
  }

  function trackGesture(event: PointerEvent<HTMLElement>) {
    const session = sessionRef.current;
    if (!session) return;
    const point = gesture.track(event);
    if (!point) return;
    event.stopPropagation();
    const next =
      session.mode === 'move'
        ? movedBox(
            session.key,
            session.start,
            point.fx - session.grab.dx,
            point.fy - session.grab.dy,
          )
        : resizedBox(session.key, session.start, session.mode, point);
    session.next = next;
    paintBox(next, session.start, session.mode);
  }

  function endGesture(event: PointerEvent<HTMLElement>) {
    if (!sessionRef.current) return;
    event.stopPropagation();
    gesture.end(event);
  }

  /* ---------------------------- keyboard ---------------------------- */

  function onLayerKey(key: LayerKey, event: KeyboardEvent<HTMLElement>) {
    if (disabled) return;
    if (event.key === 'Delete' || event.key === 'Backspace') {
      if (key.startsWith('text:') || key.startsWith('shape:')) {
        event.preventDefault();
        removeLayer(key);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      select('backdrop');
      event.currentTarget.blur();
      return;
    }
    const box = layerBox(zones, key);
    if (!box) return;
    const step = event.shiftKey ? NUDGE_STEP_LARGE : NUDGE_STEP;
    let dx = 0;
    let dy = 0;
    switch (event.key) {
      case 'ArrowLeft':
        dx = -step;
        break;
      case 'ArrowRight':
        dx = step;
        break;
      case 'ArrowUp':
        dy = -step;
        break;
      case 'ArrowDown':
        dy = step;
        break;
      default:
        return;
    }
    event.preventDefault();
    // Alt resizes from the bottom-right corner; a plain arrow moves.
    const next = event.altKey
      ? resizedBox(key, box, 'se', { fx: box.x + box.w + dx, fy: box.y + box.h + dy })
      : movedBox(key, box, box.x + dx, box.y + dy);
    onChange(withLayerBox(zones, key, next));
  }

  /* --------------------------- layer edits -------------------------- */

  function addText(role: CommunityTextRole) {
    if (zones.texts.length >= TEXTS_MAX) return;
    const text = newTextBox(zones, role);
    onChange({ ...zones, texts: [...zones.texts, text] });
    select(textKey(text.id));
  }

  function addShape(kind: CommunityShapeKind) {
    if (zones.shapes.length >= SHAPES_MAX) return;
    const shape = newShape(zones, kind);
    onChange({ ...zones, shapes: [...zones.shapes, shape] });
    select(shapeKey(shape.id));
  }

  function removeLayer(key: LayerKey) {
    const text = findText(zones, key);
    if (text) {
      // A design keeps at least one text box; the inspector says so on the button.
      if (zones.texts.length <= 1) return;
      onChange({ ...zones, texts: zones.texts.filter((entry) => entry.id !== text.id) });
      select('backdrop');
      return;
    }
    const shape = findShape(zones, key);
    if (shape) {
      onChange({ ...zones, shapes: zones.shapes.filter((entry) => entry.id !== shape.id) });
      select('backdrop');
      return;
    }
    if (key === 'portrait') onChange({ ...zones, portrait: null });
    if (key === 'value') onChange({ ...zones, value: null });
    if (key === 'logo') onChange({ ...zones, logo: { ...zones.logo, enabled: false } });
  }

  function duplicateLayer(key: LayerKey) {
    const offset = (box: ZoneBox, minimum: ZoneMinimum) =>
      clampBox({ ...box, x: box.x + 0.03, y: box.y + 0.03 }, minimum);
    const text = findText(zones, key);
    if (text && zones.texts.length < TEXTS_MAX) {
      // A copy of the headline is a second line of type, not a second headline.
      const role: CommunityTextRole = text.role === 'headline' ? 'subheading' : text.role;
      const copy: CommunityTextBox = {
        ...text,
        id: nextLayerId(zones, role),
        role,
        label: `${text.label.slice(0, 34)} copy`,
        box: offset(text.box, TEXT_MINIMUM),
      };
      const index = zones.texts.indexOf(text);
      const texts = [...zones.texts];
      texts.splice(index + 1, 0, copy);
      onChange({ ...zones, texts });
      select(textKey(copy.id));
      return;
    }
    const shape = findShape(zones, key);
    if (shape && zones.shapes.length < SHAPES_MAX) {
      const base = newShape(zones, shape.kind);
      const copy: CommunityShape = { ...shape, id: base.id, box: offset(shape.box, SHAPE_MINIMUM) };
      const index = zones.shapes.indexOf(shape);
      const shapes = [...zones.shapes];
      shapes.splice(index + 1, 0, copy);
      onChange({ ...zones, shapes });
      select(shapeKey(copy.id));
    }
  }

  /**
   * Moves a layer one step through its list. For shapes that is the drawing
   * order — later is on top — so "up" in the layers list is later in the array.
   */
  function reorderLayer(key: LayerKey, towardTop: boolean) {
    const text = findText(zones, key);
    if (text) {
      const index = zones.texts.indexOf(text);
      const target = towardTop ? index - 1 : index + 1;
      if (target < 0 || target >= zones.texts.length) return;
      const texts = [...zones.texts];
      texts.splice(index, 1);
      texts.splice(target, 0, text);
      onChange({ ...zones, texts });
      return;
    }
    const shape = findShape(zones, key);
    if (shape) {
      const index = zones.shapes.indexOf(shape);
      const target = towardTop ? index + 1 : index - 1;
      if (target < 0 || target >= zones.shapes.length) return;
      const shapes = [...zones.shapes];
      shapes.splice(index, 1);
      shapes.splice(target, 0, shape);
      onChange({ ...zones, shapes });
    }
  }

  function canReorder(key: LayerKey, towardTop: boolean): boolean {
    const text = findText(zones, key);
    if (text) {
      const index = zones.texts.indexOf(text);
      return towardTop ? index > 0 : index < zones.texts.length - 1;
    }
    const shape = findShape(zones, key);
    if (shape) {
      const index = zones.shapes.indexOf(shape);
      return towardTop ? index < zones.shapes.length - 1 : index > 0;
    }
    return false;
  }

  function setCanvas(canvas: CommunityCanvas) {
    onChange({ ...zones, canvas });
  }

  /* ----------------------------- canvas ----------------------------- */

  /** What one canvas layer shows and says; its handlers are passed alongside. */
  function layerProps(key: LayerKey, box: ZoneBox, extraClass = '') {
    const text = findText(zones, key);
    const filledBy = text !== null && isAiBox(text) ? ', filled by AI' : '';
    return {
      layerKey: key,
      box,
      extraClass,
      isSelected: key === selected,
      disabled,
      label: `${layerName(zones, key)}${filledBy}, ${percent(box.w)} by ${percent(
        box.h,
      )}, at ${percent(box.x)} across and ${percent(box.y)} down`,
    };
  }

  const imageMissing = zones.canvas.kind === 'image' && imageUrl === null;

  return (
    <div className="builder">
      <aside className="builder__layers" aria-label="Layers">
        <LayersPanel
          zones={zones}
          palette={palette}
          selected={selected}
          disabled={disabled}
          onSelect={select}
          onAddText={addText}
          onAddShape={addShape}
          onToggle={(key, on) => {
            if (key === 'portrait') onChange({ ...zones, portrait: on ? DEFAULT_PORTRAIT : null });
            if (key === 'value') onChange({ ...zones, value: on ? DEFAULT_VALUE : null });
            if (key === 'logo') onChange({ ...zones, logo: { ...zones.logo, enabled: on } });
            if (on) select(key);
          }}
        />
      </aside>

      <div className="builder__stage-column">
        <div
          className="builder-stage design-preview"
          ref={stageRef}
          data-busy={gesture.isActive ? 'true' : undefined}
          onPointerDown={(event) => {
            if (event.button === 0 && gesture.canBegin(event)) select('backdrop');
          }}
        >
          <span
            className="design-preview__backdrop"
            style={backdropStyle(zones.canvas, palette, imageUrl)}
            aria-hidden="true"
          />
          {imageMissing ? (
            <span className="builder-stage__note">
              {isLoadingImage
                ? 'Loading your design'
                : 'Image backdrop: upload or import your design in the backdrop panel.'}
            </span>
          ) : null}

          {zones.shapes.map((shape) => (
            <CanvasLayer
              key={shape.id}
              {...layerProps(shapeKey(shape.id), shape.box, 'builder-layer--shape')}
              register={registerLayer}
              onSelect={select}
              onStart={startMove}
              onTrack={trackGesture}
              onEnd={endGesture}
              onKey={onLayerKey}
            >
              <ShapeFill shape={shape} palette={palette} />
            </CanvasLayer>
          ))}

          {zones.portrait ? (
            <CanvasLayer
              {...layerProps('portrait', portraitBox(zones.portrait), 'builder-layer--circle')}
              register={registerLayer}
              onSelect={select}
              onStart={startMove}
              onTrack={trackGesture}
              onEnd={endGesture}
              onKey={onLayerKey}
            >
              <PortraitFill />
            </CanvasLayer>
          ) : null}

          {zones.value ? (
            <CanvasLayer
              {...layerProps('value', zones.value)}
              register={registerLayer}
              onSelect={select}
              onStart={startMove}
              onTrack={trackGesture}
              onEnd={endGesture}
              onKey={onLayerKey}
            >
              <ValueFill value={null} palette={palette} backdropLuma={luma} />
            </CanvasLayer>
          ) : null}

          {zones.texts.map((text) => (
            <CanvasLayer
              key={text.id}
              {...layerProps(
                textKey(text.id),
                text.box,
                isAiBox(text) ? 'builder-layer--text builder-layer--ai' : 'builder-layer--text',
              )}
              register={registerLayer}
              onSelect={select}
              onStart={startMove}
              onTrack={trackGesture}
              onEnd={endGesture}
              onKey={onLayerKey}
            >
              <TextFill
                text={text}
                value={text.default}
                palette={palette}
                backdropLuma={luma}
                emptyLabel={text.role === 'headline' ? 'Topic or caption lands here' : text.label}
              />
              {/* Studio only: the gallery tile and the brief draw the design, not its wiring. */}
              {isAiBox(text) ? (
                <span className="builder-ai-badge builder-layer__ai-badge" aria-hidden="true">
                  AI
                </span>
              ) : null}
            </CanvasLayer>
          ))}

          {zones.logo.enabled ? (
            <CanvasLayer
              {...layerProps('logo', zones.logo.box)}
              register={registerLayer}
              onSelect={select}
              onStart={startMove}
              onTrack={trackGesture}
              onEnd={endGesture}
              onKey={onLayerKey}
            >
              <LogoFill logoUrl={logoUrl} style={zones.logo.style} />
            </CanvasLayer>
          ) : null}

          {selectedBox ? (
            <div
              className={
                selected === 'portrait' ? 'builder-frame builder-frame--circle' : 'builder-frame'
              }
              ref={frameRef}
              style={boxStyle(selectedBox)}
              aria-hidden="true"
            >
              <span className="builder-frame__tag">{layerName(zones, selected)}</span>
              {disabled
                ? null
                : CORNERS.map((corner) => (
                    <span
                      key={corner}
                      className={`builder-frame__handle builder-frame__handle--${corner}`}
                      title={`Resize from the ${CORNER_NAMES[corner]} corner`}
                      onPointerDown={(event) => startResize(corner, event)}
                      onPointerMove={trackGesture}
                      onPointerUp={endGesture}
                      onPointerCancel={endGesture}
                    />
                  ))}
            </div>
          ) : null}
        </div>

        <div className="builder__notes">
          <p className="builder__note">Drag to move, drag a corner to resize.</p>
          <BuilderMore label="Keyboard shortcuts">
            <dl className="builder-keys">
              <dt>Tab</dt>
              <dd>Focus the next layer</dd>
              <dt>Arrows</dt>
              <dd>Move 1%, with Shift 5%</dd>
              <dt>Alt + arrows</dt>
              <dd>Resize</dd>
              <dt>Delete</dt>
              <dd>Remove a text box or shape</dd>
              <dt>Escape</dt>
              <dd>Select the backdrop</dd>
            </dl>
          </BuilderMore>
          <p className="builder__note builder__note--muted">
            Sample text in your brand colours. The server render is the final picture.
          </p>
        </div>
      </div>

      <section className="builder__inspector" aria-label="Selected layer">
        <CommunityLayerInspector
          zones={zones}
          layer={selected}
          palette={palette}
          disabled={disabled}
          imageSource={imageSource}
          canMoveUp={canReorder(selected, true)}
          canMoveDown={canReorder(selected, false)}
          onChange={onChange}
          onCanvasChange={setCanvas}
          onDuplicate={() => duplicateLayer(selected)}
          onRemove={() => removeLayer(selected)}
          onReorder={(towardTop) => reorderLayer(selected, towardTop)}
        />
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Canvas layer                                                        */
/* ------------------------------------------------------------------ */

interface CanvasLayerProps {
  layerKey: LayerKey;
  box: ZoneBox;
  extraClass: string;
  isSelected: boolean;
  disabled: boolean;
  label: string;
  register: (key: LayerKey, element: HTMLElement | null) => void;
  onSelect: (key: LayerKey) => void;
  onStart: (key: LayerKey, event: PointerEvent<HTMLElement>) => void;
  onTrack: (event: PointerEvent<HTMLElement>) => void;
  onEnd: (event: PointerEvent<HTMLElement>) => void;
  onKey: (key: LayerKey, event: KeyboardEvent<HTMLElement>) => void;
  children: ReactNode;
}

/**
 * One layer on the canvas: its content, placed by its box, focusable, and the
 * thing a pointer grabs. Selection is drawn by the frame above every layer, so
 * an obscured layer still shows where it is.
 */
function CanvasLayer({
  layerKey,
  box,
  extraClass,
  isSelected,
  disabled,
  label,
  register,
  onSelect,
  onStart,
  onTrack,
  onEnd,
  onKey,
  children,
}: CanvasLayerProps) {
  return (
    <div
      ref={(element) => register(layerKey, element)}
      className={[
        'builder-layer',
        extraClass,
        isSelected ? 'builder-layer--selected' : '',
        disabled ? 'builder-layer--locked' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={boxStyle(box)}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={label}
      onFocus={() => onSelect(layerKey)}
      onPointerDown={(event) => onStart(layerKey, event)}
      onPointerMove={onTrack}
      onPointerUp={onEnd}
      onPointerCancel={onEnd}
      onKeyDown={(event) => onKey(layerKey, event)}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Layers panel                                                        */
/* ------------------------------------------------------------------ */

interface LayersPanelProps {
  zones: CommunityZonesV2;
  palette: BrandPalette;
  selected: LayerKey;
  disabled: boolean;
  onSelect: (key: LayerKey) => void;
  onAddText: (role: CommunityTextRole) => void;
  onAddShape: (kind: CommunityShapeKind) => void;
  onToggle: (key: 'logo' | 'portrait' | 'value', on: boolean) => void;
}

/** A menu behind one dashed "+ Add" row: a `details` that closes itself once used. */
function AddMenu({
  label,
  disabled,
  children,
}: {
  label: string;
  disabled: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);
  return (
    <details
      className="builder-add"
      open={isOpen && !disabled}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary
        className="builder-add__summary"
        aria-disabled={disabled}
        onClick={(event) => {
          if (disabled) event.preventDefault();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') close();
        }}
      >
        <BuilderIcon name="plus" />
        {label}
      </summary>
      <div className="builder-add__menu">{children(close)}</div>
    </details>
  );
}

/** The small tile at the left of a layer row: the kind's icon, or its colour. */
function LayerTile({
  icon,
  fill,
  shape = 'square',
  dot,
}: {
  icon?: BuilderIconName;
  /** A CSS background: the layer's own colour or gradient. */
  fill?: string;
  shape?: CommunityShapeKind | 'square';
  /** A pinned text colour, as a dot in the tile's corner. */
  dot?: string | null;
}) {
  return (
    <span className="builder-layer-tile" aria-hidden="true">
      {icon ? <BuilderIcon name={icon} /> : null}
      {fill ? (
        <span
          className={`builder-layer-tile__fill builder-layer-tile__fill--${shape}`}
          style={{ background: fill }}
        />
      ) : null}
      {dot ? <span className="builder-layer-tile__dot" style={{ background: dot }} /> : null}
    </span>
  );
}

function LayerRow({
  tile,
  name,
  detail,
  isSelected,
  isOff = false,
  onSelect,
  toggle,
}: {
  tile: ReactNode;
  name: string;
  detail: string;
  isSelected: boolean;
  isOff?: boolean;
  onSelect: () => void;
  toggle?: ReactNode;
}) {
  return (
    <li
      className={[
        'builder-layer-row',
        isSelected ? 'builder-layer-row--selected' : '',
        isOff ? 'builder-layer-row--off' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        className="builder-layer-row__main"
        type="button"
        aria-current={isSelected ? 'true' : undefined}
        onClick={onSelect}
      >
        {tile}
        <span className="builder-layer-row__text">
          <span className="builder-layer-row__name">{name}</span>
          <span className="builder-layer-row__detail">{detail}</span>
        </span>
      </button>
      {toggle}
    </li>
  );
}

/** A text row's second line: its role, unless the name already says it, and who fills it. */
function textDetail(text: CommunityTextBox, name: string): string {
  const role = TEXT_ROLES[text.role].label;
  const filler = isAiBox(text) ? 'AI fills it' : 'Member fills it';
  return name === role ? filler : `${role} · ${filler}`;
}

function GroupHead({ title, count }: { title: string; count?: string }) {
  return (
    <div className="builder-layers__head">
      <span className="builder-layers__title">{title}</span>
      {count ? <span className="builder-layers__count">{count}</span> : null}
    </div>
  );
}

function LayersPanel({
  zones,
  palette,
  selected,
  disabled,
  onSelect,
  onAddText,
  onAddShape,
  onToggle,
}: LayersPanelProps) {
  const hasHeadline = zones.texts.some((text) => text.role === 'headline');
  // Top of the list is top of the picture: shapes are drawn in array order.
  const shapesTopFirst = [...zones.shapes].reverse();
  const canvas = zones.canvas;

  function toggle(key: 'logo' | 'portrait' | 'value', on: boolean, name: string) {
    return (
      <span className="builder-layer-row__toggle">
        <Switch
          label={`Show ${name.toLowerCase()}`}
          hideLabel
          checked={on}
          disabled={disabled}
          onChange={(checked) => onToggle(key, checked)}
        />
      </span>
    );
  }

  return (
    <div className="builder-layers">
      <div className="builder-layers__group">
        <GroupHead title="Logo and photo" />
        <ul className="builder-layers__list">
          <LayerRow
            tile={<LayerTile icon="logo" />}
            name="Logo"
            detail={
              zones.logo.enabled ? `${LOGO_STYLE_LABELS[zones.logo.style].label} style` : 'Off'
            }
            isSelected={selected === 'logo'}
            isOff={!zones.logo.enabled}
            onSelect={() => onSelect('logo')}
            toggle={toggle('logo', zones.logo.enabled, 'Logo')}
          />
          <LayerRow
            tile={<LayerTile icon="photo" />}
            name="Portrait circle"
            detail={zones.portrait ? 'Photo from the post' : 'Off'}
            isSelected={selected === 'portrait'}
            isOff={zones.portrait === null}
            onSelect={() => (zones.portrait ? onSelect('portrait') : onToggle('portrait', true))}
            toggle={toggle('portrait', zones.portrait !== null, 'Portrait circle')}
          />
          <LayerRow
            tile={<LayerTile icon="value" />}
            name="Value box"
            detail={zones.value ? 'A price or a number' : 'Off'}
            isSelected={selected === 'value'}
            isOff={zones.value === null}
            onSelect={() => (zones.value ? onSelect('value') : onToggle('value', true))}
            toggle={toggle('value', zones.value !== null, 'Value box')}
          />
        </ul>
      </div>

      <div className="builder-layers__group">
        <GroupHead title="Text boxes" count={`${zones.texts.length}/${TEXTS_MAX}`} />
        <ul className="builder-layers__list">
          {zones.texts.map((text) => (
            <LayerRow
              key={text.id}
              tile={
                <LayerTile
                  icon="text"
                  dot={text.color === 'auto' ? null : textInk(text.color, palette, 0)}
                />
              }
              name={layerName(zones, textKey(text.id))}
              detail={textDetail(text, layerName(zones, textKey(text.id)))}
              isSelected={selected === textKey(text.id)}
              onSelect={() => onSelect(textKey(text.id))}
            />
          ))}
        </ul>
        <AddMenu label="Add text box" disabled={disabled || zones.texts.length >= TEXTS_MAX}>
          {(close) =>
            TEXT_ROLE_ORDER.map((role) => {
              const blocked = role === 'headline' && hasHeadline;
              return (
                <button
                  key={role}
                  className="builder-add__item"
                  type="button"
                  disabled={blocked}
                  title={blocked ? 'A design has one headline at most.' : undefined}
                  onClick={() => {
                    onAddText(role);
                    close();
                  }}
                >
                  {TEXT_ROLES[role].label}
                </button>
              );
            })
          }
        </AddMenu>
      </div>

      <div className="builder-layers__group">
        <GroupHead title="Shapes" count={`${zones.shapes.length}/${SHAPES_MAX}`} />
        {shapesTopFirst.length > 0 ? (
          <ul className="builder-layers__list">
            {shapesTopFirst.map((shape) => (
              <LayerRow
                key={shape.id}
                tile={<LayerTile fill={resolveColor(shape.fill, palette)} shape={shape.kind} />}
                name={layerName(zones, shapeKey(shape.id))}
                detail={`${colorName(shape.fill)} · ${Math.round(shape.opacity * 100)}%`}
                isSelected={selected === shapeKey(shape.id)}
                onSelect={() => onSelect(shapeKey(shape.id))}
              />
            ))}
          </ul>
        ) : null}
        <AddMenu label="Add shape" disabled={disabled || zones.shapes.length >= SHAPES_MAX}>
          {(close) =>
            (['rect', 'ellipse', 'line'] as const).map((kind) => (
              <button
                key={kind}
                className="builder-add__item"
                type="button"
                onClick={() => {
                  onAddShape(kind);
                  close();
                }}
              >
                {SHAPE_KIND_LABELS[kind]}
              </button>
            ))
          }
        </AddMenu>
      </div>

      <div className="builder-layers__group">
        <GroupHead title="Backdrop" />
        <ul className="builder-layers__list">
          <LayerRow
            tile={
              canvas.kind === 'image' ? (
                <LayerTile icon="photo" />
              ) : (
                <LayerTile
                  fill={
                    canvas.kind === 'color'
                      ? resolveColor(canvas.color, palette)
                      : `linear-gradient(${canvas.angle}deg, ${resolveColor(
                          canvas.from,
                          palette,
                        )}, ${resolveColor(canvas.to, palette)})`
                  }
                />
              )
            }
            name="Backdrop"
            detail={
              canvas.kind === 'image'
                ? 'Your image'
                : canvas.kind === 'color'
                  ? colorName(canvas.color)
                  : `Gradient, ${canvas.angle} degrees`
            }
            isSelected={selected === 'backdrop'}
            onSelect={() => onSelect('backdrop')}
          />
        </ul>
      </div>
    </div>
  );
}
